import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ArrowRight, Loader2, AlertTriangle, CalendarClock, CalendarMinus } from 'lucide-react';

/**
 * Read-only preview of THE canonical SLA re-derive for a planned due-date change,
 * shown after the admin picks a new date but BEFORE the PUT /projects/{id} fires.
 *
 * Direction-aware (server sets `preview.direction`):
 *   - "later"   → insert-behind re-pack. Tail may slide FORWARD to preserve SLA
 *                 spacing behind the anchor's new position. Copy: "re-spaced so no
 *                 deadline is compressed."
 *   - "earlier" → gap-close uniform shift. Every pre-draft project on this editor's
 *                 queue slides EARLIER by the anchor delta so downstream clients get
 *                 their films SOONER. Rows that would land in the past floor at
 *                 TODAY and are flagged (`today_floored`); any resulting same-date
 *                 collision carries a `collision_group` badge for operator review.
 *   - "same"    → no-op (unchanged, empty moves).
 *
 * Decisions:
 *   1. "Apply — re-sequence N" (primary)  → PUT with cascade (writes M + re-derive)
 *   2. "Move only this project" (secondary)→ PUT skip_cascade=true (M only, audited)
 *   3. "Cancel" (neutral)                  → no PUT at all (cancel-means-nothing)
 *
 * Error state (`preview.error === true`): the preview fetch failed. Buttons are
 * "Move only this project", "Retry", "Cancel" — never a blind apply.
 */
const fmt = (d) => {
  if (!d) return '—';
  try {
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return d; }
};

export default function CascadePreviewDialog({
  open, onOpenChange, preview, projectTitle, submitting,
  onConfirmCascade, onConfirmSkip, onCancel, onRetry, onLaterModeChange,
}) {
  const isError = preview?.error === true;
  const anchor = preview?.anchor || {};
  const moves = preview?.moves || [];
  const n = moves.length;
  const newDate = preview?.new_date || anchor?.new_due || '';
  const oldDate = anchor?.old_due || preview?.old_date || '';
  const title = projectTitle || anchor?.title || 'this project';
  const direction = preview?.direction || 'later';   // "later" | "earlier" | "same"
  const isEarlier = direction === 'earlier';
  const deltaDays = preview?.delta_days || 0;
  const flooredRows = moves.filter(m => m.today_floored).length;
  const collisionKeys = new Set(moves.filter(m => m.collision_group).map(m => m.collision_group));
  const anyCollision = collisionKeys.size > 0;
  const preAnchorSameDate = preview?.pre_anchor_same_date || [];
  // A later move past jobs that were behind it (operator, 2026-10-05): name them and ask. "push" moves everything
  // behind this project later by the same days; "leave" keeps their dates, so they now come first.
  const jumped = (!isError && !isEarlier && preview?.jumped) || [];
  const laterMode = preview?.later_mode || 'push';
  const isPushBack = jumped.length > 0 && laterMode === 'push';
  const laterDays = deltaDays || (oldDate && newDate
    ? Math.round((new Date(newDate + 'T00:00:00') - new Date(oldDate + 'T00:00:00')) / 86400000) : 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg" data-testid="cascade-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base" data-testid="cascade-preview-title">
            {isEarlier
              ? <CalendarMinus className="w-4 h-4 text-oxidized-teal" data-testid="cascade-preview-icon-earlier" />
              : <CalendarClock className="w-4 h-4 text-oxidized-teal" data-testid="cascade-preview-icon-later" />}
            {isError
              ? 'Schedule preview unavailable'
              : isEarlier ? 'Pull queue earlier' : isPushBack ? 'Push the queue back' : 'Re-sequence editor queue'}
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm" data-testid="cascade-preview-description">
            {isError ? (
              'Could not compute the schedule preview. Retry, move only this project, or cancel.'
            ) : isEarlier ? (
              <>Pulling <span className="text-zinc-200">{title}</span>{' '}
              <span className="font-mono text-zinc-300">{fmt(oldDate)}</span>{' '}
              <ArrowRight className="inline w-3 h-3" />{' '}
              <span className="font-mono text-zinc-200">{fmt(newDate)}</span>{' '}
              <span className="text-oxidized-teal">({deltaDays}d earlier)</span>. Only projects
              scheduled <span className="text-zinc-200">after this one</span> in the editor&rsquo;s
              queue slide earlier by the same amount &mdash; downstream clients get their films{' '}
              <span className="text-zinc-200">sooner</span>. Projects already ahead of this one
              stay put. SLA gaps preserved.</>
            ) : isPushBack ? (
              <>Moving <span className="text-zinc-200">{title}</span>{' '}
              <span className="font-mono text-zinc-300">{fmt(oldDate)}</span>{' '}
              <ArrowRight className="inline w-3 h-3" />{' '}
              <span className="font-mono text-zinc-200">{fmt(newDate)}</span>{' '}
              <span className="text-orange-300">({laterDays}d later)</span>. Every job that was behind it moves later
              by the same {laterDays} days, so the order stays the same. Jobs ahead of it stay put.</>
            ) : (
              <>Moving <span className="text-zinc-200">{title}</span>{' '}
              <span className="font-mono text-zinc-300">{fmt(oldDate)}</span>{' '}
              <ArrowRight className="inline w-3 h-3" />{' '}
              <span className="font-mono text-zinc-200">{fmt(newDate)}</span>. The queue is re-spaced by each
              project&rsquo;s turnaround (SLA) and the editor&rsquo;s recorded time off &mdash; each
              row says why it moves. Some moves repair spacing that was already too tight.</>
            )}
          </DialogDescription>
        </DialogHeader>

        {isError ? (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-300 flex items-start gap-2" data-testid="cascade-error-block">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            {/* Prefer the server's reason when it gave one. A 400 from
                cascade-preview names the records with unreadable due dates, so
                the operator can fix the data instead of filing a bug. Generic
                copy is the fallback for 500s/network failures. */}
            <span data-testid="cascade-error-message">
              {preview?.error_detail
                ? `${preview.error_detail} You can still move only this project without touching the queue.`
                : 'The schedule preview failed to load. You can move only this project without touching the queue.'}
            </span>
          </div>
        ) : (
          // min-w-0: a row's reason line is truncated, but its full width still sized the dialog's grid column, so a
          // long reason pushed every row past the dialog's right edge (seen with the jumped-job reasons, 2026-10-05)
          <div className="space-y-3 min-w-0">
            {jumped.length > 0 && (
              <div className="rounded-md border border-orange-400/30 bg-orange-400/5 p-3 space-y-2" data-testid="later-mode-block">
                <div className="text-xs text-orange-200" data-testid="later-mode-jumped">
                  This moves <span className="text-zinc-100">{title}</span> past {jumped.length} job{jumped.length === 1 ? '' : 's'} that
                  {jumped.length === 1 ? ' was' : ' were'} due after it:{' '}
                  {jumped.map((j, i) => (
                    <span key={j.project_id}>
                      <span className="text-zinc-100">{j.title || j.project_id}</span>{' '}
                      <span className="font-mono text-zinc-400">({fmt(j.old_due)})</span>{i < jumped.length - 1 ? ', ' : '.'}
                    </span>
                  ))}
                </div>
                <div className="flex flex-col gap-1.5">
                  <button type="button" data-testid="later-mode-push" disabled={submitting}
                          onClick={() => onLaterModeChange && onLaterModeChange('push')}
                          className={`text-left rounded border px-2.5 py-1.5 text-xs transition-colors ${laterMode === 'push'
                            ? 'border-oxidized-teal bg-oxidized-teal/10 text-zinc-100' : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'}`}>
                    <span className="font-semibold">Push them back too</span> (usual): everything behind {title} moves {laterDays} days
                    later, in the same order.
                  </button>
                  <button type="button" data-testid="later-mode-leave" disabled={submitting}
                          onClick={() => onLaterModeChange && onLaterModeChange('leave')}
                          className={`text-left rounded border px-2.5 py-1.5 text-xs transition-colors ${laterMode === 'leave'
                            ? 'border-oxidized-teal bg-oxidized-teal/10 text-zinc-100' : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'}`}>
                    <span className="font-semibold">Leave them where they are</span>: they keep their dates and now come before {title}.
                  </button>
                </div>
              </div>
            )}
            <div className="rounded-md border border-oxidized-teal/30 bg-oxidized-teal/5 px-3 py-2 flex items-center justify-between" data-testid="resequence-anchor-row">
              <span className="text-sm text-zinc-200 truncate mr-3">{anchor?.title || title}</span>
              <span className="font-mono text-xs text-oxidized-teal whitespace-nowrap">
                {isEarlier ? 'lands →' : 'stays →'} {fmt(newDate)}
              </span>
            </div>
            <div className="flex items-center justify-between px-1">
              <span className="text-xs uppercase tracking-wide text-zinc-500">
                {isEarlier ? 'Tail after this project' : isPushBack ? 'Behind this project' : 'Following projects'}
              </span>
              <span className="font-mono text-xs text-zinc-300" data-testid="resequence-move-count">
                {n === 0
                  ? 'nothing else moves'
                  : isEarlier
                    ? `${n} project${n === 1 ? '' : 's'} pulled earlier`
                    : isPushBack
                      ? `${n} pushed back ${laterDays}d`
                      : `${n} project${n === 1 ? '' : 's'} re-spaced`}
              </span>
            </div>
            {(flooredRows > 0 || anyCollision || preAnchorSameDate.length > 0) && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-300 flex items-start gap-2" data-testid="cascade-warning-block">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <div>
                  {flooredRows > 0 && (
                    <div data-testid="cascade-today-floor-warning">
                      {flooredRows} project{flooredRows === 1 ? '' : 's'} would have landed
                      in the past &mdash; floored to today. Review before applying.
                    </div>
                  )}
                  {anyCollision && (
                    <div data-testid="cascade-collision-warning" className={flooredRows > 0 ? 'mt-1' : ''}>
                      Same-date collision on {collisionKeys.size} date{collisionKeys.size === 1 ? '' : 's'} &mdash;
                      operator can apply as-is or fix source data first.
                    </div>
                  )}
                  {preAnchorSameDate.length > 0 && (
                    <div data-testid="cascade-pre-anchor-collision-warning" className={(flooredRows > 0 || anyCollision) ? 'mt-1' : ''}>
                      Landing on {fmt(newDate)} which already holds{' '}
                      {preAnchorSameDate.map((p, i) => (
                        <span key={p.project_id}>
                          <span className="text-zinc-100">{p.title || p.project_id}</span>
                          {i < preAnchorSameDate.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                      {' '}(unchanged &mdash; ahead of this project in the queue). Same-date landing.
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="max-h-56 overflow-y-auto rounded-md border border-zinc-800 divide-y divide-zinc-800" data-testid="cascade-affected-list">
              {n === 0 ? (
                <div className="p-4 text-center text-[11px] text-slate-500" data-testid="cascade-no-impact">
                  {isEarlier
                    ? 'No projects sit after this one on the editor\u2019s queue \u2014 only this project moves.'
                    : 'Queue already correctly spaced \u2014 only this project moves.'}
                </div>
              ) : moves.map((m) => (
                <div key={m.project_id}
                     className="flex items-center justify-between px-3 py-2"
                     data-testid={`resequence-row-${m.project_id}`}>
                  <span className="text-sm text-zinc-300 truncate mr-3 min-w-0">
                    <span className="flex items-center gap-1.5">
                      {m.title || m.project_id}
                      {m.today_floored && (
                        <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40"
                              data-testid={`resequence-floor-badge-${m.project_id}`}>
                          floored
                        </span>
                      )}
                      {m.jumped && (
                        <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-orange-400/20 text-orange-300 border border-orange-400/40"
                              data-testid={`resequence-jumped-badge-${m.project_id}`}>
                          jumped
                        </span>
                      )}
                      {m.collision_group && (
                        <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40"
                              data-testid={`resequence-collision-badge-${m.project_id}`}>
                          collision
                        </span>
                      )}
                    </span>
                    {/* WHY this row moves — from the spacing engine itself, so the
                        operator can tell an SLA-repair from a vacation dodge from
                        genuine ripple. Without it, moves chained off projects NOT
                        shown in this list (unchanged rows are hidden) look like
                        the anchor move caused them. */}
                    {m.reason && (
                      <span className="block text-[10px] text-slate-500 truncate mt-0.5"
                            data-testid={`resequence-reason-${m.project_id}`}>
                        {m.reason}
                      </span>
                    )}
                  </span>
                  <span className="font-mono text-xs whitespace-nowrap text-zinc-400">
                    {fmt(m.old_due)} <ArrowRight className={`inline w-3 h-3 ${isEarlier ? 'text-oxidized-teal' : 'text-orange-400'}`} />{' '}
                    <span className={isEarlier ? 'text-oxidized-teal' : 'text-orange-300'}>{fmt(m.new_due)}</span>
                  </span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-zinc-500">
              Only this editor&rsquo;s in-progress (pre-draft) projects move. Review / approved projects are never touched. No emails are sent.
            </p>
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onCancel} disabled={submitting} data-testid="cascade-cancel" className="text-zinc-400 hover:text-zinc-200">
            Cancel
          </Button>
          <Button variant="outline" onClick={onConfirmSkip} disabled={submitting} data-testid="cascade-confirm-skip" className="border-zinc-700 text-zinc-200">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Move only this project'}
          </Button>
          {isError ? (
            <Button onClick={onRetry} disabled={submitting} data-testid="cascade-retry" className="bg-oxidized-teal text-zinc-950 hover:bg-oxidized-teal/90">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Retry'}
            </Button>
          ) : (
            <Button onClick={onConfirmCascade} disabled={submitting} data-testid="cascade-confirm-all" className="bg-oxidized-teal text-zinc-950 hover:bg-oxidized-teal/90">
              {submitting
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : (n === 0
                    ? 'Apply'
                    : isEarlier
                      ? `Apply \u2014 pull ${n} earlier`
                      : isPushBack
                        ? `Apply, push ${n} back`
                        : `Apply \u2014 re-sequence ${n}`)}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
